import React from 'react';
import {mapRealtimePayloadToViewModel} from './sourcesAdapter';
import {fetchSourcesList} from './sourcesApi';
import {createSourcesRealtimeSubscription} from './sourcesRealtime';
import {readSourcesCache, writeSourcesCache} from './sourcesCacheAdapter';

function isBrowserOffline() {
    if (typeof navigator === 'undefined') {
        return false;
    }

    return navigator.onLine === false;
}

export default function useSourcesDataSync({
    isActive,
    activeView,
    userId,
    setSources,
    setConnectionState,
    setIsLoading,
    setError,
    setWarning,
    setIsStale,
}) {
    const pendingLoadsRef = React.useRef(0);

    React.useEffect(() => {
        if (typeof window === 'undefined') {
            return () => {};
        }

        const handleOnline = () => {
            setWarning(null);
        };

        window.addEventListener('online', handleOnline);
        return () => {
            window.removeEventListener('online', handleOnline);
        };
    }, [setWarning]);

    const loadSources = React.useCallback(async (viewToLoad = activeView, options = {}) => {
        if (!userId) {
            return;
        }

        const {signal, suppressError = false, preferCache = false} = options;
        pendingLoadsRef.current += 1;
        setIsLoading(true);

        try {
            if (preferCache) {
                try {
                    const cachedEntry = await readSourcesCache(viewToLoad);
                    if (cachedEntry) {
                        setSources(mapRealtimePayloadToViewModel(viewToLoad, cachedEntry.payload));
                        setIsStale(false);
                        setWarning(null);
                        setError(null);
                    }
                } catch (cacheReadError) {
                    console.warn('[SourcesSection] Failed to read source cache:', cacheReadError);
                }
            }

            const payload = await fetchSourcesList({
                view: viewToLoad,
                userId,
                signal,
            });

            setSources(mapRealtimePayloadToViewModel(viewToLoad, payload));
            setIsStale(false);
            setWarning(null);
            setError(null);

            writeSourcesCache(viewToLoad, payload).catch((cacheWriteError) => {
                console.warn('[SourcesSection] Failed to write source cache:', cacheWriteError);
            });
        } catch (err) {
            if (err?.name === 'AbortError') {
                return;
            }

            try {
                const cachedEntry = await readSourcesCache(viewToLoad);
                if (cachedEntry) {
                    setSources(mapRealtimePayloadToViewModel(viewToLoad, cachedEntry.payload));
                    setIsStale(true);
                    setWarning(isBrowserOffline() ? 'Offline. Showing cached sources.' : null);
                    setError(null);
                    return;
                }
            } catch (cacheReadError) {
                console.warn('[SourcesSection] Failed to read source cache:', cacheReadError);
            }

            setIsStale(false);
            setWarning(null);
            console.error('[SourcesSection] Failed to load source items:', err);
            if (!suppressError) {
                setError('Failed to load sources. Please check the console logs.');
            }
            throw err;
        } finally {
            pendingLoadsRef.current = Math.max(0, pendingLoadsRef.current - 1);
            setIsLoading(pendingLoadsRef.current > 0);
        }
    }, [activeView, setError, setIsLoading, setIsStale, setSources, setWarning, userId]);

    React.useEffect(() => {
        if (!isActive) {
            setConnectionState('idle');
            setIsLoading(false);
            return;
        }

        setConnectionState('connecting');
        setIsLoading(true);
        setError(null);
        setWarning(null);
        // Keep the previous view until the new payload arrives so empty copy cannot flash.
        setIsStale(true);

        let isDisposed = false;
        const abortController = new AbortController();
        loadSources(activeView, {
            signal: abortController.signal,
            suppressError: isDisposed,
            preferCache: true,
        }).catch(() => {
        });

        const unsubscribe = createSourcesRealtimeSubscription({
            view: activeView,
            userId,
            onConnect: () => {
                setConnectionState('connected');
                setError(null);
            },
            onMessage: () => {
                loadSources(activeView, {suppressError: true}).catch(() => {
                });
            },
            onError: () => {
                setConnectionState('error');
                if (isBrowserOffline()) {
                    setWarning('Offline. Live updates unavailable.');
                }
            },
            onClose: () => {
                setConnectionState((currentState) => (currentState === 'error' ? currentState : 'closed'));
            },
        });

        return () => {
            isDisposed = true;
            abortController.abort();
            unsubscribe?.();
        };
    }, [activeView, isActive, loadSources, setConnectionState, setError, setIsLoading, setIsStale, setSources, setWarning, userId]);

    return {loadSources};
}
